import React, { useState, useEffect } from 'react';
import { BarChart3, Building2, Users, DollarSign, TrendingUp, Download, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { companyPaymentsAPI, companiesAPI, customersAPI, settingsAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';

const PaymentAnalysis = () => {
  const { t } = useTranslation();
  const [companyPayments, setCompanyPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('TRY');
  const [analysisView, setAnalysisView] = useState('company'); // 'company' or 'customer'
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExchangeRates = async () => {
    try {
      const settings = await settingsAPI.getAll();
      setExchangeRates({
        USD: parseFloat(settings.exchange_rate_usd) || 1,
        EUR: parseFloat(settings.exchange_rate_eur) || 1,
        GBP: parseFloat(settings.exchange_rate_gbp) || 1,
        GOLD: parseFloat(settings.exchange_rate_gold) || 1
      });
    } catch (error) {
      console.error('Exchange rates fetch error:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [paymentsData, companiesData, customersData] = await Promise.all([
        companyPaymentsAPI.getAll(),
        companiesAPI.getAll(),
        customersAPI.getAll()
      ]);
      
      setCompanyPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error(t('paymentAnalysis.fetchError') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  // Para birimine göre dönüştür
  const convertToCurrency = (amount, fromCurrency) => {
    if (!amount || amount === 0) return 0;
    const amountNum = parseFloat(amount) || 0;
    
    if (fromCurrency === selectedCurrency) {
      return amountNum;
    }
    
    // Önce TL'ye çevir
    let amountInTRY = amountNum;
    if (fromCurrency !== 'TRY') {
      const rate = exchangeRates[fromCurrency] || 1;
      amountInTRY = amountNum * rate;
    }
    
    // Sonra seçilen para birimine çevir
    if (selectedCurrency === 'TRY') {
      return amountInTRY;
    } else {
      const rate = exchangeRates[selectedCurrency] || 1;
      return amountInTRY / rate;
    }
  };

  // Para birimi sembolü
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'GOLD': 'Altın'
    };
    return symbols[currency] || currency;
  };

  // Firmaya göre analiz
  const getCompanyAnalysis = () => {
    const companyMap = {};

    companyPayments
      .filter(p => p.is_active !== false)
      .forEach(payment => {
        const companyId = payment.company_id;
        const company = companies.find(c => c.id === companyId);
        const companyName = company?.name || t('paymentAnalysis.unknownCompany');
        
        if (!companyMap[companyId]) {
          companyMap[companyId] = {
            companyId,
            companyName,
            totalPurchase: 0,
            totalSale: 0,
            paymentCount: 0,
            payments: []
          };
        }

        // Yıllık toplam hesapla
        let purchase = convertToCurrency(payment.purchase_price || 0, payment.purchase_currency || 'TRY');
        let sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
        
        if (payment.frequency === 'monthly') {
          purchase *= 12;
          sale *= 12;
        }

        companyMap[companyId].totalPurchase += purchase;
        companyMap[companyId].totalSale += sale;
        companyMap[companyId].paymentCount++;
        companyMap[companyId].payments.push({
          ...payment,
          purchaseAmount: purchase,
          saleAmount: sale,
          profitAmount: sale - purchase
        });
      });

    return Object.values(companyMap)
      .map(company => ({
        ...company,
        profit: company.totalSale - company.totalPurchase
      }))
      .sort((a, b) => b.totalSale - a.totalSale);
  };

  // Müşteriye göre analiz
  const getCustomerAnalysis = () => {
    const customerMap = {};

    // Önce müşterilerin hangi firmalara bağlı olduğunu bul
    const customerCompanyMap = {};
    customers.forEach(customer => {
      // customer_companies tablosundan ilişkileri al (eğer varsa)
      // Şimdilik customer'ın company_id'sini kullan
      if (customer.company_id) {
        if (!customerCompanyMap[customer.id]) {
          customerCompanyMap[customer.id] = [];
        }
        customerCompanyMap[customer.id].push(customer.company_id);
      }
    });

    // Her payment için müşteriyi bul
    companyPayments
      .filter(p => p.is_active !== false)
      .forEach(payment => {
        // Payment'in company_id'sine göre müşterileri bul
        const relatedCustomers = customers.filter(c => {
          if (c.company_id === payment.company_id) return true;
          // customer_companies ilişkisini kontrol et (eğer varsa)
          return customerCompanyMap[c.id]?.includes(payment.company_id);
        });

        if (relatedCustomers.length === 0) {
          // Müşteri bulunamadı, genel olarak ekle
          const key = 'no-customer';
          if (!customerMap[key]) {
            customerMap[key] = {
              customerId: null,
              customerName: t('paymentAnalysis.unassignedCustomer'),
              totalPurchase: 0,
              totalSale: 0,
              paymentCount: 0,
              payments: []
            };
          }
          
          let purchase = convertToCurrency(payment.purchase_price || 0, payment.purchase_currency || 'TRY');
          let sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
          
          if (payment.frequency === 'monthly') {
            purchase *= 12;
            sale *= 12;
          }

          customerMap[key].totalPurchase += purchase;
          customerMap[key].totalSale += sale;
          customerMap[key].paymentCount++;
          customerMap[key].payments.push({
            ...payment,
            purchaseAmount: purchase,
            saleAmount: sale,
            profitAmount: sale - purchase
          });
        } else {
          // Her müşteri için ekle
          relatedCustomers.forEach(customer => {
            const customerId = customer.id;
            const customerName = customer.full_name || customer.name || t('paymentAnalysis.unknownCustomer');
            
            if (!customerMap[customerId]) {
              customerMap[customerId] = {
                customerId,
                customerName,
                totalPurchase: 0,
                totalSale: 0,
                paymentCount: 0,
                payments: []
              };
            }

            let purchase = convertToCurrency(payment.purchase_price || 0, payment.purchase_currency || 'TRY');
            let sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
            
            if (payment.frequency === 'monthly') {
              purchase *= 12;
              sale *= 12;
            }

            customerMap[customerId].totalPurchase += purchase;
            customerMap[customerId].totalSale += sale;
            customerMap[customerId].paymentCount++;
            customerMap[customerId].payments.push({
              ...payment,
              purchaseAmount: purchase,
              saleAmount: sale,
              profitAmount: sale - purchase
            });
          });
        }
      });

    return Object.values(customerMap)
      .map(customer => ({
        ...customer,
        profit: customer.totalSale - customer.totalPurchase
      }))
      .sort((a, b) => b.totalSale - a.totalSale);
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleExport = () => {
    const data = analysisView === 'company' ? getCompanyAnalysis() : getCustomerAnalysis();
    
    const dataToExport = data.flatMap(item => {
      const rows = [{
        [analysisView === 'company' ? t('paymentAnalysis.company') : t('paymentAnalysis.customer')]: item[analysisView === 'company' ? 'companyName' : 'customerName'],
        [t('paymentAnalysis.totalPurchase')]: item.totalPurchase,
        [t('paymentAnalysis.totalSale')]: item.totalSale,
        [t('paymentAnalysis.totalProfit')]: item.profit,
        [t('paymentAnalysis.paymentCount')]: item.paymentCount
      }];
      
      // Detayları ekle
      item.payments.forEach(payment => {
        rows.push({
          [analysisView === 'company' ? t('paymentAnalysis.company') : t('paymentAnalysis.customer')]: '',
          [t('paymentAnalysis.packageName')]: payment.package_name || '',
          [t('paymentAnalysis.packageType')]: payment.package_type || '',
          [t('paymentAnalysis.frequency')]: payment.frequency === 'monthly' ? t('common.monthly') : payment.frequency === 'yearly' ? t('common.yearly') : t('common.oneTime'),
          [t('paymentAnalysis.purchase')]: payment.purchaseAmount,
          [t('paymentAnalysis.sale')]: payment.saleAmount,
          [t('paymentAnalysis.profit')]: payment.profitAmount
        });
      });
      
      return rows;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('paymentAnalysis.sheetName'));
    
    XLSX.writeFile(wb, `payment_analysis_${analysisView}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success(t('paymentAnalysis.exportSuccess'));
  };

  const companyData = getCompanyAnalysis();
  const customerData = getCustomerAnalysis();
  const currentData = analysisView === 'company' ? companyData : customerData;

  const totalStats = currentData.reduce((acc, item) => {
    acc.totalPurchase += item.totalPurchase;
    acc.totalSale += item.totalSale;
    acc.totalProfit += item.profit;
    acc.totalPayments += item.paymentCount;
    return acc;
  }, { totalPurchase: 0, totalSale: 0, totalProfit: 0, totalPayments: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 bg-gray-50 dark:bg-gray-900 min-h-full">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('paymentAnalysis.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('paymentAnalysis.subtitle')}</p>
          </div>
          <button 
            onClick={handleExport}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <Download className="h-5 w-5" />
            <span className="font-medium">Export</span>
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('paymentAnalysis.currency')}
            </label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="TRY">{t('currency.try')}</option>
              <option value="USD">{t('currency.usd')}</option>
              <option value="EUR">{t('currency.eur')}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('paymentAnalysis.analysisView')}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setAnalysisView('company')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  analysisView === 'company'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Building2 className="inline h-4 w-4 mr-2" />
                {t('paymentAnalysis.byCompany')}
              </button>
              <button
                onClick={() => setAnalysisView('customer')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  analysisView === 'customer'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Users className="inline h-4 w-4 mr-2" />
                {t('paymentAnalysis.byCustomer')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toplam İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('paymentAnalysis.totalPurchase')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('paymentAnalysis.totalSale')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('paymentAnalysis.totalProfit')}</p>
              <p className={`text-2xl font-bold mt-1 ${
                totalStats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('paymentAnalysis.totalPayments')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {totalStats.totalPayments}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Analiz Tablosu */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {analysisView === 'company' ? t('paymentAnalysis.byCompanyAnalysis') : t('paymentAnalysis.byCustomerAnalysis')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-8 sm:w-12">
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {analysisView === 'company' ? t('paymentAnalysis.company') : t('paymentAnalysis.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('paymentAnalysis.purchase')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('paymentAnalysis.sale')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('paymentAnalysis.profit')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('paymentAnalysis.paymentCount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentData.map((item) => {
                const isExpanded = expandedItems.has(item[analysisView === 'company' ? 'companyId' : 'customerId']);
                return (
                  <React.Fragment key={item[analysisView === 'company' ? 'companyId' : 'customerId']}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleExpand(item[analysisView === 'company' ? 'companyId' : 'customerId'])}
                          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {item[analysisView === 'company' ? 'companyName' : 'customerName']}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {getCurrencySymbol(selectedCurrency)} {item.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {getCurrencySymbol(selectedCurrency)} {item.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${
                          item.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {getCurrencySymbol(selectedCurrency)} {item.profit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{item.paymentCount}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleExpand(item[analysisView === 'company' ? 'companyId' : 'customerId'])}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {isExpanded ? t('paymentAnalysis.hide') : t('paymentAnalysis.details')}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="7" className="px-4 py-4 bg-gray-50 dark:bg-gray-700">
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">{t('paymentAnalysis.paymentDetails')}</h4>
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-100 dark:bg-gray-600">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.packageName')}</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.type')}</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.frequency')}</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.purchase')}</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.sale')}</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('paymentAnalysis.profit')}</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {item.payments.map((payment, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{payment.package_name || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{payment.package_type || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                      {payment.frequency === 'monthly' ? t('common.monthly') : payment.frequency === 'yearly' ? t('common.yearly') : t('common.oneTime')}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                      {getCurrencySymbol(selectedCurrency)} {payment.purchaseAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                      {getCurrencySymbol(selectedCurrency)} {payment.saleAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={`px-4 py-2 text-sm font-semibold ${
                                      payment.profitAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                    }`}>
                                      {getCurrencySymbol(selectedCurrency)} {payment.profitAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {currentData.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('paymentAnalysis.noPayments')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('paymentAnalysis.noPaymentsDesc')}</p>
        </div>
      )}
    </div>
  );
};

export default PaymentAnalysis;

